import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FormaPago from './FormaPago';
import API from '../../../utils/api';

/**
 * Wrapper para FormaPago que obtiene los datos del pedido y los pasa como props
 */
const FormaPagoConDatos = () => {
  const { idPedido } = useParams();
  const navigate = useNavigate();
  
  const [datosPedido, setDatosPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const cargarDatosPedido = async () => {
      if (!idPedido) {
        setError('No se proporcionó ID de pedido');
        setLoading(false);
        return;
      }

      try {
        console.log("🔍 Cargando datos del pedido:", idPedido);
        
        // Primero intentar obtener de localStorage
        const datosLocalStorage = localStorage.getItem('datosPago');
        if (datosLocalStorage) {
          try {
            const datos = JSON.parse(datosLocalStorage);
            if (datos.idPedido === parseInt(idPedido)) {
              console.log("📦 Datos encontrados en localStorage:", datos);
              setDatosPedido(datos);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.warn("⚠️ Error al parsear localStorage:", e);
          }
        }

        // Si no hay datos en localStorage, obtener del API
        console.log("🌐 Obteniendo datos del API...");
        console.log("🔍 Llamando API.pedidos.getPedido con ID:", idPedido);
        
        const respuesta = await API.pedidos.getPedido(idPedido);
        console.log("📋 Respuesta completa del API:", respuesta);
        console.log("🔧 Tipo de respuesta:", typeof respuesta);
        console.log("🔧 Respuesta.pedido:", respuesta?.pedido);
        
        // La respuesta viene directamente como el objeto del pedido (API.pedidos.getPedido ya procesa response.data)
        if (respuesta && respuesta.idPedido) {
          console.log("✅ Datos encontrados - respuesta directa del pedido");
          const pedido = respuesta; // La respuesta ES el pedido directamente
          const detalles = pedido.detalles || []; // Los detalles están dentro del pedido

          console.log("📋 Pedido completo:", pedido);
          console.log("📋 Total del pedido desde BD:", pedido.total);
          console.log("📋 Detalles:", detalles.map(d => ({
            nombre: d.nombre,
            precio: d.precio,
            precioFinal: d.precioFinal,
            subtotal: d.subtotal,
            cantidad: d.cantidad
          })));

          // Calcular total - prioridad:
          // 1. Usar el total del pedido si existe y es válido (ya calculado con promociones)
          // 2. Calcular sumando subtotales/precioFinal de cada item
          // 3. Fallback: precio * cantidad
          let subtotalCalculado = 0;

          // Si el pedido tiene total guardado, usarlo directamente
          if (pedido.total !== undefined && pedido.total !== null && parseFloat(pedido.total) > 0) {
            subtotalCalculado = parseFloat(pedido.total);
            console.log("💰 Usando total del pedido desde BD:", subtotalCalculado);
          } else {
            // Calcular desde los detalles
            subtotalCalculado = detalles.reduce((sum, item) => {
              const cantidad = parseInt(item.cantidad || 1);

              // Prioridad: subtotal > precioFinal * cantidad > precio * cantidad
              if (item.subtotal !== undefined && item.subtotal !== null) {
                return sum + parseFloat(item.subtotal);
              }
              if (item.precioFinal !== undefined && item.precioFinal !== null) {
                return sum + (parseFloat(item.precioFinal) * cantidad);
              }
              return sum + (parseFloat(item.precio || 0) * cantidad);
            }, 0);
            console.log("💰 Subtotal calculado desde detalles:", subtotalCalculado);
          }

          // Formatear productos para pasarlos a FormaPago
          const productosFormateados = detalles.map(item => {
            const cantidad = parseInt(item.cantidad || 1);
            // Usar precioFinal si existe, sino precio
            const precioEfectivo = item.precioFinal !== undefined && item.precioFinal !== null
              ? parseFloat(item.precioFinal)
              : parseFloat(item.precio || 0);
            const subtotalItem = item.subtotal !== undefined && item.subtotal !== null
              ? parseFloat(item.subtotal)
              : precioEfectivo * cantidad;

            return {
              ...item,
              precio: precioEfectivo, // Usar precio efectivo (con promoción si existe)
              precioOriginal: parseFloat(item.precio || 0), // Guardar precio original
              cantidad: cantidad,
              subtotal: subtotalItem
            };
          });

          const datosFormateados = {
            idPedido: parseInt(idPedido),
            totalCuenta: subtotalCalculado,
            productos: productosFormateados,
            pedidoInfo: {
              idPedido: parseInt(idPedido),
              numeroMesa: pedido.mesa?.numeroMesa || pedido.numeroMesa || pedido.idMesa,
              nombreMesa: pedido.mesa?.nombreMesa || pedido.nombreMesa || `Mesa ${pedido.mesa?.numeroMesa || pedido.numeroMesa || pedido.idMesa}`,
              mesero: pedido.mesero?.nombre || pedido.mesero || pedido.nombreMesero,
              numeroPersonas: pedido.numeroPersonas || 1,
              detalles: detalles,
              descuentos: 0
            },
            subtotal: subtotalCalculado,
            total: subtotalCalculado
          };
          
          console.log("✅ Datos formateados del API:", datosFormateados);
          setDatosPedido(datosFormateados);
        } else {
          throw new Error('No se pudieron obtener los datos del pedido');
        }
        
      } catch (err) {
        console.error("❌ Error al cargar datos del pedido:", err);
        setError(`Error al cargar el pedido: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    cargarDatosPedido();
  }, [idPedido]);

  const handleCancelar = () => {
    // Limpiar localStorage y regresar
    localStorage.removeItem('datosPago');
    navigate(-1);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Cargando datos del pedido...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column' 
      }}>
        <div>Error: {error}</div>
        <button onClick={handleCancelar} style={{ marginTop: '10px' }}>
          Regresar
        </button>
      </div>
    );
  }

  if (!datosPedido) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>No se encontraron datos del pedido</div>
      </div>
    );
  }

  return (
    <FormaPago
      totalCuenta={datosPedido.totalCuenta}
      pedidoInfo={datosPedido.pedidoInfo}
      idPedido={datosPedido.idPedido}
      productos={datosPedido.productos}
      descuentosPromociones={datosPedido.descuentos || 0}
      promocionesAplicadas={datosPedido.promocionesAplicadas || []}
      onCancelar={handleCancelar}
    />
  );
};

export default FormaPagoConDatos;